import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useField } from "@unform/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "../../../shared/hooks";
import { clientService } from "../../../shared/services/api/client/clientService";

type TAutoCompleteOption = { id: string; label: string };

interface IAutoCompleteClientLazyProps {
	isExternalLoading?: boolean;
	labelSelect?: string;
	namecampo?: string; // nome do campo no Unform
	disabled?: boolean;
	idSelected?: (id: string) => void;
	nameDefault?: string; // rótulo do valor inicial (quando já vem id)
	minChars?: number; // mínimo de caracteres para buscar (default: 2)
}

export const AutoCompleteClientLazy: React.FC<IAutoCompleteClientLazyProps> = ({
	isExternalLoading = false,
	labelSelect = "",
	namecampo = "clientId",
	idSelected,
	disabled = false,
	nameDefault = "",
	minChars = 2,
}) => {
	// Unform
	const { fieldName, registerField, defaultValue, error, clearError } = useField(namecampo);
	const { debounce } = useDebounce();

	// Estado / Refs
	const [selectedId, _setSelectedId] = useState<string | undefined>((defaultValue as string | undefined) ?? undefined);
	const selectedIdRef = useRef<string | undefined>(selectedId);
	const setSelectedId = (id?: string) => {
		selectedIdRef.current = id;
		_setSelectedId(id);
	};

	const [busca, setBusca] = useState("");
	const [debouncedBusca, setDebouncedBusca] = useState("");
	const [options, setOptions] = useState<TAutoCompleteOption[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const resolvedInitialRef = useRef(false); // evita resolver valor inicial 2x
	const suppressNextSearchRef = useRef(false); // evita busca causada por seleção/reclick
	const reqSeq = useRef(0); // evita race entre respostas
	const lastSelectedRef = useRef<TAutoCompleteOption | null>(null);

	// Registro no Unform
	useEffect(() => {
		registerField({
			name: fieldName,
			getValue: () => selectedIdRef.current,
			setValue: (_, newSelectedId) => setSelectedId(newSelectedId),
			clearValue: () => setSelectedId(undefined),
		});
	}, [registerField, fieldName]);

	// Debounce do INPUT
	useEffect(() => {
		const cancel = debounce(() => setDebouncedBusca((busca || "").trim()));
		return () => {
			void cancel;
		};
	}, [busca]);

	// Resolver valor inicial UMA vez (id + nameDefault)
	useEffect(() => {
		if (!selectedIdRef.current || !nameDefault || resolvedInitialRef.current) return;
		resolvedInitialRef.current = true; // trava já aqui (evita 2 execuções no StrictMode)

		setIsLoading(true);
		const myReq = ++reqSeq.current;

		(async () => {
			try {
				const result = await clientService.getAll(1, nameDefault, "", "", "active");
				if (!(result instanceof Error)) {
					const mapped = result.data.map((c: any) => ({ id: c.id, label: c.name })) as TAutoCompleteOption[];
					const hasSelected = mapped.some((o) => o.id === selectedIdRef.current);
					if (myReq === reqSeq.current) {
						setOptions(hasSelected ? mapped : [...mapped, { id: selectedIdRef.current as string, label: nameDefault }]);
					}
				}
			} finally {
				if (myReq === reqSeq.current) setIsLoading(false);
			}
		})();
	}, [nameDefault]);

	// Busca por digitação usando o VALOR DEBOUNCED
	useEffect(() => {
		const term = debouncedBusca;

		// Se a mudança do input veio de uma seleção/reclick, ignore uma vez
		if (suppressNextSearchRef.current) {
			suppressNextSearchRef.current = false;
			return;
		}

		// Sem termo suficiente: não buscar nada
		if (term.length < minChars) {
			setOptions((prev) => {
				// mantém ghost option para o selecionado, se necessário
				if (selectedIdRef.current && nameDefault && !prev.some((o) => o.id === selectedIdRef.current)) {
					return [{ id: selectedIdRef.current, label: nameDefault }];
				}
				return [];
			});
			return;
		}

		setIsLoading(true);
		const myReq = ++reqSeq.current;

		(async () => {
			try {
				const result = await clientService.getAll(1, term, "", "", "active");
				if (!(result instanceof Error)) {
					const mapped = result.data.map((c: any) => ({ id: c.id, label: c.name })) as TAutoCompleteOption[];
					if (myReq === reqSeq.current) setOptions(mapped);
				}
			} finally {
				if (myReq === reqSeq.current) setIsLoading(false);
			}
		})();
	}, [debouncedBusca, minChars, nameDefault]);

	// ✅ Valor atual do Autocomplete (objeto) — com fallback para a última seleção
	const valueOption = useMemo(() => {
		const id = selectedIdRef.current;
		if (!id) return null;

		// 1) Tenta achar nas opções atuais
		const found = options.find((o) => o.id === id);
		if (found) return found;

		// 2) Se a última seleção guarda o mesmo id, use-a
		if (lastSelectedRef.current?.id === id) return lastSelectedRef.current;

		// 3) Se existe rótulo default (edição), use ghost com nameDefault
		if (nameDefault) return { id, label: nameDefault } as TAutoCompleteOption;

		// 4) Fallback neutro (não recomendado — de preferência sempre teremos 1, 2 ou 3)
		return { id, label: "" } as TAutoCompleteOption;
	}, [options, nameDefault]);

	// Lista exibida (garante ghost quando preciso)
	const displayedOptions = useMemo(() => {
		const val = valueOption;
		if (val && !options.some((o) => o.id === val.id)) {
			return [val, ...options];
		}
		return options;
	}, [options, valueOption]);

	return (
		<Autocomplete<TAutoCompleteOption, false, false, false>
			openText="Abrir"
			closeText="Fechar"
			noOptionsText="Nenhuma opção"
			loadingText="Carregando…"
			disablePortal
			value={valueOption}
			options={displayedOptions}
			getOptionLabel={(o) => o?.label ?? ""}
			isOptionEqualToValue={(opt, val) => opt.id === val.id}
			loading={isLoading}
			size="small"
			fullWidth
			disabled={isExternalLoading || disabled}
			onInputChange={(_, newInput, reason) => {
				// Ao selecionar/limpar, o MUI chama onInputChange com reason 'reset'/'clear' — ignorar
				if (reason === "reset" || reason === "clear") {
					return;
				}
				if (suppressNextSearchRef.current) {
					suppressNextSearchRef.current = false;
					return;
				}
				if (error) clearError?.();
				setBusca((newInput || "").trimStart());
			}}
			onChange={(_, newValue) => {
				const id = newValue?.id;

				// Se clicou na mesma opção já selecionada, não faz nada e mantém flag para suprimir 'reset'
				if (id === selectedIdRef.current) {
					suppressNextSearchRef.current = true;
					return;
				}

				// Seleção nova: fixa o id, guarda a opção para exibição e suprime onInputChange('reset')
				suppressNextSearchRef.current = true;
				lastSelectedRef.current = newValue ?? null; // ✅ guarda {id, label} selecionado
				setSelectedId(id || undefined);
				if (id) idSelected?.(id);
				clearError?.();
			}}
			popupIcon={isExternalLoading || isLoading ? <CircularProgress size={28} /> : undefined}
			renderInput={(params) => <TextField {...params} label={labelSelect} error={!!error} helperText={error} disabled={disabled} fullWidth />}
		/>
	);
};
